import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { LoadingBar } from '../ui/LoadingBar';
import { LOADING_LAYOUT, loadingLogoWidthPx, loadingBarWidthPx } from '../../ui/loadingLayout';

/**
 * Pantalla de carga inicial (overlay con logo + barra de progreso): la crea, actualiza el progreso
 * de la barra y la oculta cuando el mundo está listo (mapa cargado + superficies + player local
 * spawneado). Extraído de GameScene (god-object). El estado (loadingScreen + flags de readiness) vive
 * en GameScene (host) y se accede vía `this.scene.*`.
 */
export class LoadingScreenController {
  constructor(private scene: GameScene) {}

  public createLoadingScreen(): void {
    // Idempotente: el preload (progress) y create() la pueden llamar varias veces.
    if (this.scene.loadingScreen) return;

    const { width, height } = this.scene.scale;

    // Fondo negro que cubre toda la pantalla
    const bg = this.scene.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 1)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(10000); // Muy alto para estar por encima de todo

    // Layout compartido con la pantalla de login (ver client/src/ui/loadingLayout.ts):
    // logo arriba, gap, barra abajo, todo el grupo centrado en pantalla. Mismas medidas
    // que el DOM del login para que las dos pantallas de carga se vean idénticas.
    const haveBar =
      this.scene.textures.exists('loading_frame') && this.scene.textures.exists('loading_fill');
    const barW = loadingBarWidthPx(width);
    const barH = haveBar ? barW / LOADING_LAYOUT.barAspect : 0;
    const gap = haveBar ? LOADING_LAYOUT.gapPx : 0;

    // Logo (intenta cargar logo.png, si falla usa texto como fallback). Se crea en
    // (0,0) y se reposiciona abajo una vez conocido su alto para centrar el grupo.
    let logo: Phaser.GameObjects.Image | Phaser.GameObjects.Text;
    let shadow: Phaser.GameObjects.Image | undefined;
    const logoTexture = this.scene.textures.exists('logo') ? this.scene.textures.get('logo') : null;

    if (logoTexture && logoTexture.key !== '__MISSING') {
      logo = this.scene.add.image(0, 0, 'logo').setOrigin(0.5).setScrollFactor(0).setDepth(10001);
      // Escalar al ancho objetivo de la spec (mantiene proporción).
      const scale = loadingLogoWidthPx(width) / Math.max(1, logo.width);
      logo.setScale(scale);

      // Glow negro EXTERNO (halo parejo alrededor del logo), como en las pantallas
      // de login. WebGL preFX; en Canvas es no-op y queda la sombra de abajo.
      if (logo.preFX) {
        logo.preFX.setPadding(20);
        logo.preFX.addGlow(0x000000, 4, 0, false, 0.1, 14);
      }

      // Sombra: mismo logo offset +2,+2, tint negro suave (más prolijo que el FX de Phaser).
      shadow = this.scene.add
        .image(0, 0, 'logo')
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(10000)
        .setTint(0x000000)
        .setAlpha(0.3)
        .setScale(scale);
    } else {
      // Logo no cargado / textura inválida → fallback de texto.
      logo = this.createLogoTextFallback(0, 0);
    }

    // Centrar verticalmente el grupo (logo + gap + barra) en la pantalla.
    const logoH = (logo as { displayHeight?: number }).displayHeight ?? 60;
    const groupH = logoH + gap + barH;
    const top = height / 2 - groupH / 2;
    const logoCenterY = top + logoH / 2;

    logo.setPosition(width / 2, logoCenterY);
    if (shadow) shadow.setPosition(width / 2 + 2, logoCenterY + 2);

    this.scene.loadingScreen = shadow ? { bg, logo, shadow } : { bg, logo };

    // Barra de carga debajo del logo, separada por `gap` (mismo stack que el login).
    if (haveBar) {
      const barCenterY = top + logoH + gap + barH / 2;
      const bar = new LoadingBar(this.scene, {
        centerX: width / 2,
        centerY: barCenterY,
        width: barW,
        depth: 10001,
        fillOffsetY: 0, // alineado con la barra DOM del login (fill centrado en el frame)
      });
      this.scene.loadingScreen.bar = bar;
      this.scene.loadingBarMaxProgress = 0; // reset al construir la pantalla
      bar.setProgress(this.scene.loadingBarMaxProgress);
    }

    // Fade-in suave: al pasar del panel de personaje al juego, el logo/barra
    // aparecían de golpe ("chocaba a la vista"). El canvas ya es negro (#000000),
    // así que el fondo negro arranca opaco (cobertura inmediata) y solo el
    // logo + barra (+ sombra a su 0.3) entran con un fundido. Espejo del fade-out
    // de checkAndHideLoadingScreen.
    const FADE_IN_MS = 400;
    const fadeInTargets: Phaser.GameObjects.GameObject[] = [logo];
    if (this.scene.loadingScreen.bar) {
      fadeInTargets.push(...this.scene.loadingScreen.bar.getObjects());
    }
    this.scene.tweens.add({
      targets: fadeInTargets,
      alpha: { from: 0, to: 1 },
      duration: FADE_IN_MS,
      ease: 'Power2',
    });
    if (shadow) {
      this.scene.tweens.add({
        targets: shadow,
        alpha: { from: 0, to: 0.3 },
        duration: FADE_IN_MS,
        ease: 'Power2',
      });
    }
  }

  /**

   * Crea un logo de texto como fallback cuando no se puede cargar la imagen

   */

  public createLogoTextFallback(x: number, y: number): Phaser.GameObjects.Text {
    const text = this.scene.addText(x, y, 'Aureum', {
        fontSize: '48px',

        color: '#ffffff',

        fontStyle: 'bold',

        stroke: '#000000',

        strokeThickness: 4,

        shadow: {
          offsetX: 2,

          offsetY: 2,

          color: '#000000',

          blur: 4,

          stroke: true,

          fill: true,
        },
      })

      .setOrigin(0.5)

      .setScrollFactor(0)

      .setDepth(10001);

    return text;
  }

  /**

   * Verifica si todo está cargado y oculta la pantalla de carga con fade out

   */

  /** Actualiza la loading bar de forma monotónica (nunca baja). */
  public setLoadingBarProgress(value: number): void {
    const v = Phaser.Math.Clamp(value, 0, 1);
    if (v <= this.scene.loadingBarMaxProgress) return;
    this.scene.loadingBarMaxProgress = v;
    this.scene.loadingScreen?.bar?.setProgress(v);
    // Puentear el progreso al orquestador externo (main.ts): alimenta la barra DOM
    // que reemplazó al botón "Entrar" en la pantalla de selección durante la
    // transición al juego. Ver docs/AUTH_AND_LOGIN.md (focus cross-fade).
    this.scene.game.events.emit('game:progress', v);
  }

  public checkAndHideLoadingScreen(): void {
    // Si ya se ocultó, no hacer nada

    if (this.scene.isLoadingComplete || !this.scene.loadingScreen) {
      return;
    }

    // Verificar que todo esté listo:
    // 1. Mapa cargado (currentMap no null)

    // 2. Personaje local renderizado (localPlayerFollowTarget no null)

    // 3. Conexión establecida (room y localSessionId no null)

    const isMapLoaded = this.scene.currentMap !== null;

    const isPlayerLoaded = this.scene.localPlayerFollowTarget !== null;

    const isConnected = this.scene.room !== null && this.scene.localSessionId !== null;

    // 4. Surfaces (pasto) horneados y renderizados → evita el flash de tiles negros.
    const areSurfacesReady = this.scene.surfacesReady;

    // 5. Foto inicial de entidades del AOI (players/NPCs/nodos que había al joinear) ya creada por
    //    completo. El burst se drena de a poco desde update() (no de golpe → sin freeze en "Entrar");
    //    no revelamos el mundo hasta que terminó. Ver GameScene.drainInitialEntityQueue().
    const areEntitiesReady = this.scene.entitiesPopulated;

    // Avanzar la barra por hitos post-preload (preload llegó a 0.7). El drenado de entidades tiene su
    // propio tramo de progreso (0.85→1.0 en drainInitialEntityQueue), así que acá lo contamos como
    // hito binario para no pisar ese avance más fino.
    const milestones =
      (isMapLoaded ? 1 : 0) +
      (isPlayerLoaded ? 1 : 0) +
      (isConnected ? 1 : 0) +
      (areSurfacesReady ? 1 : 0) +
      (areEntitiesReady ? 1 : 0);
    this.setLoadingBarProgress(0.7 + (milestones / 5) * 0.3);

    if (!(isMapLoaded && isPlayerLoaded && isConnected && areSurfacesReady && areEntitiesReady)) {
      this.scene.loadingReadyFrames = 0; // algo todavía no está → reiniciar el settle
      return;
    }

    // Todo listo: la barra llega a 1, pero NO arrancamos el fade en el mismo frame. Esperamos
    // unos frames con el overlay opaco para que el primer render pesado del mundo se complete
    // por detrás → el fade no compite con ese trabajo y queda fluido.
    this.setLoadingBarProgress(1);
    this.scene.loadingReadyFrames++;
    if (this.scene.loadingReadyFrames < GameScene.LOADING_SETTLE_FRAMES) {
      return;
    }

    this.scene.isLoadingComplete = true;

    // Avisar al orquestador externo (main.ts) que el mundo ya está cargado y
    // renderizado: dispara el focus cross-fade que revela la ventana del juego
    // desde la pantalla de selección. Ver docs/AUTH_AND_LOGIN.md.
    this.scene.game.events.emit('game:ready');

    // Fade out de la pantalla de carga

    const targets: Phaser.GameObjects.GameObject[] = [this.scene.loadingScreen.bg, this.scene.loadingScreen.logo];

    // Incluir la sombra si existe

    if (this.scene.loadingScreen.shadow) {
      targets.push(this.scene.loadingScreen.shadow);
    }

    // Incluir la barra de carga en el fade.
    if (this.scene.loadingScreen.bar) {
      targets.push(...this.scene.loadingScreen.bar.getObjects());
    }

    this.scene.tweens.add({
      targets,

      alpha: 0,

      duration: 500,

      ease: 'Power2',

      onComplete: () => {
        // Ocultar completamente después del fade out

        this.scene.loadingScreen?.bg.setVisible(false);

        this.scene.loadingScreen?.logo.setVisible(false);

        if (this.scene.loadingScreen?.shadow) {
          this.scene.loadingScreen.shadow.setVisible(false);
        }

        this.scene.loadingScreen?.bar?.destroy();

        // Recién ahora (fade COMPLETO) habilitamos los controles. Durante toda la carga y el
        // fade el input estuvo deshabilitado para que no se mueva/clickee nada a medias.
        this.scene.setControlsEnabled(true);
      },
    });
  }
}
